import { useId, useState, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  apiFetch,
  cn,
  formatUsPhone,
  type PortalPreferences,
  type PortalPreferencesPatch,
} from "@dearbill/shared";
import { HomeCard } from "../home/HomeCard";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const NOTE = "text-body-sm text-slate-teal";
const SAVE_ERROR = "We couldn't save your preference. Please try again.";
const STOP_ERROR = "We couldn't turn text messages on.";

type Channel = keyof PortalPreferencesPatch;

interface PreferencesCardProps {
  /** The `/me` read's preferences; the card then follows its own PATCH responses. */
  initial: PortalPreferences;
  /** The patient's email; null disables the email switch. */
  email: string | null;
  /** The patient's mobile number (E.164), named in the text-message captions. */
  phone: string;
  /** Portal session bearer for the PATCH. */
  session: string;
  /** Called when the PATCH is rejected with 401. */
  onSignOut: () => void;
}

/**
 * The notification channels. Each switch saves itself through `PATCH
 * /api/portal/preferences`: it flips at once and ignores clicks while its
 * request is in flight, then takes the server's state for its channel, or
 * reverts with an error alert. A 409 on turning texts on means the mobile
 * texted STOP, so the text switch locks with the START caption. A 401 signs
 * the member out.
 */
export function PreferencesCard({
  initial,
  email,
  phone,
  session,
  onSignOut,
}: PreferencesCardProps): ReactNode {
  const [prefs, setPrefs] = useState(initial);
  const [saving, setSaving] = useState<Record<Channel, boolean>>({
    emailNotifications: false,
    smsNotifications: false,
  });
  const [error, setError] = useState<string | null>(null);
  const formattedPhone = formatUsPhone(phone);

  async function save(channel: Channel, on: boolean): Promise<void> {
    setError(null);
    setPrefs((p) => ({ ...p, [channel]: on }));
    setSaving((s) => ({ ...s, [channel]: true }));
    try {
      const body =
        channel === "emailNotifications" ? { emailNotifications: on } : { smsNotifications: on };
      const saved = await apiFetch<PortalPreferences>(
        "/api/portal/preferences",
        { method: "PATCH", body: body satisfies PortalPreferencesPatch },
        { baseUrl: API_BASE, token: session },
      );
      // Each response is authoritative only for the channel it wrote: the other
      // switch may have its own request in flight, and its optimistic state stays.
      setPrefs((p) =>
        channel === "emailNotifications"
          ? { ...p, emailNotifications: saved.emailNotifications }
          : {
              ...p,
              smsNotifications: saved.smsNotifications,
              smsOptedOutByText: saved.smsOptedOutByText,
            },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return;
      }
      const stopped =
        channel === "smsNotifications" && err instanceof ApiError && err.status === 409;
      setPrefs((p) => ({ ...p, [channel]: !on, ...(stopped ? { smsOptedOutByText: true } : {}) }));
      setError(stopped ? STOP_ERROR : SAVE_ERROR);
    } finally {
      setSaving((s) => ({ ...s, [channel]: false }));
    }
  }

  return (
    <HomeCard title="Communication Preferences" titleSize="lg">
      <p className={NOTE}>
        Choose how you want to receive program updates, eligibility reviews, and document upload
        reminder alerts.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <PreferenceRow
          title="Email notifications"
          caption={
            email === null
              ? "No email on file"
              : `Deliver updates and reminders directly to ${email}`
          }
          checked={email !== null && prefs.emailNotifications}
          disabled={email === null || saving.emailNotifications}
          onToggle={() => void save("emailNotifications", !prefs.emailNotifications)}
        />
        <PreferenceRow
          title="Text messages"
          caption={
            prefs.smsOptedOutByText
              ? `You replied STOP to our texts. Reply START from ${formattedPhone} to turn them back on.`
              : `Send notification alerts to ${formattedPhone}`
          }
          checked={prefs.smsNotifications}
          disabled={prefs.smsOptedOutByText || saving.smsNotifications}
          onToggle={() => void save("smsNotifications", !prefs.smsNotifications)}
        />
      </div>
      {error !== null && <Alert tone="error" title={error} onDismiss={() => setError(null)} />}
    </HomeCard>
  );
}

interface PreferenceRowProps {
  title: string;
  caption: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}

/**
 * One channel: a switch named by the row title and described by the caption,
 * so a locked switch announces why. A disabled switch uses `aria-disabled`, not
 * `disabled`, so it keeps focus mid-save and stays in the tab order; the click
 * handler ignores it.
 */
function PreferenceRow({
  title,
  caption,
  checked,
  disabled,
  onToggle,
}: PreferenceRowProps): ReactNode {
  const titleId = useId();
  const captionId = useId();
  return (
    <div className="border-haze bg-ice flex items-start gap-4 rounded-lg border p-4">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled || undefined}
        aria-labelledby={titleId}
        aria-describedby={captionId}
        onClick={() => {
          if (!disabled) onToggle();
        }}
        className={cn(
          "relative mt-0.5 h-6 w-11 shrink-0 cursor-pointer rounded-full",
          checked ? "bg-deep-navy" : "bg-steel-blue",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 size-5 rounded-full bg-white",
            checked ? "right-0.5" : "left-0.5",
          )}
        />
      </button>
      <div className="flex flex-col gap-0.5">
        <p id={titleId} className="text-body-sm text-midnight font-semibold">
          {title}
        </p>
        <p id={captionId} className={NOTE}>
          {caption}
        </p>
      </div>
    </div>
  );
}
